import type { FC, ReactNode } from 'react';
import React, { useCallback } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import type {
  BatchReceiptDetails,
  ReceiptBatchFormValues,
} from './ReceiptBatchForm';
import { Button, Card, Divider, Stack, Typography } from '@mui/material';
import { Iconify } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import {
  addNewReceiptWithFeeRedistribution,
  removeReceiptWithFeeRedistribution,
} from './utils/receiptFees';
import type { Gender } from '@pflegenavi/shared/api';

export interface ReceiptBatchRenderRowProps {
  receipt: BatchReceiptDetails;
  index: number;
  removeRow: (index: number) => void;
  setShownImageId: (imgId: string | undefined) => void;
  setPreviewImageIds: (imgIds: string[]) => void;
  // The row attaches this to the note input's Tab handler only when isLastRow
  // is true, so pressing Tab on the last row appends a new one.
  onTabAddRow: () => void;
  isLastRow: boolean;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  handleEntryRecurringItemCalculation?: (
    entryId: string,
    index: number,
    amount: number
  ) => void;
  showAdditionalFees: boolean;
  showIncludedFees: boolean;
}

interface ReceiptBatchReceiptsProps {
  renderRow: (props: ReceiptBatchRenderRowProps) => ReactNode;
  setShownImageId: (imgId: string | undefined) => void;
  setPreviewImageIds: (imgIds: string[]) => void;
  ReceiptBatchQuickResidentSelection?: React.ReactNode;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  handleEntryRecurringItemCalculation?: (
    entryId: string,
    index: number,
    amount: number
  ) => void;
}

export const ReceiptBatchReceiptsView: FC<ReceiptBatchReceiptsProps> = ({
  renderRow,
  setShownImageId,
  setPreviewImageIds,
  ReceiptBatchQuickResidentSelection,
  archivedResidents,
  handleEntryRecurringItemCalculation,
}) => {
  const { t } = useTranslation();
  const {
    values: { receipts, cashTransactionGroup },
    setFieldValue,
  } = useFormikContext<ReceiptBatchFormValues>();

  const handleAddRow = useCallback(() => {
    const newReceipts = addNewReceiptWithFeeRedistribution(
      receipts,
      cashTransactionGroup?.separateFeeAmount
        ? -cashTransactionGroup.separateFeeAmount
        : 0,
      cashTransactionGroup?.immediateFeeAmount
        ? -cashTransactionGroup.immediateFeeAmount
        : 0
    );

    setFieldValue('receipts', newReceipts);
  }, [
    receipts,
    setFieldValue,
    cashTransactionGroup?.separateFeeAmount,
    cashTransactionGroup?.immediateFeeAmount,
  ]);

  const showAdditionalFees = Boolean(cashTransactionGroup?.separateFeeAmount);
  const showIncludedFees = Boolean(cashTransactionGroup?.immediateFeeAmount);

  return (
    <Card sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{ mb: ReceiptBatchQuickResidentSelection !== null ? 3 : 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('receipts.batch.form.details')}
        </Typography>
        {ReceiptBatchQuickResidentSelection}
      </Stack>
      <Divider
        sx={{
          width: '100%',
          mb: 4,
        }}
      />

      <FieldArray name="receipts">
        {({ remove: _remove, push: _push }) => (
          <div>
            {receipts.map((receipt, index) => {
              return renderRow({
                index,
                receipt,
                removeRow: (index: number) => {
                  const newReceipts = removeReceiptWithFeeRedistribution(
                    receipts,
                    index,
                    cashTransactionGroup?.separateFeeAmount
                      ? -cashTransactionGroup.separateFeeAmount
                      : 0,
                    cashTransactionGroup?.immediateFeeAmount
                      ? -cashTransactionGroup.immediateFeeAmount
                      : 0
                  );

                  setFieldValue('receipts', newReceipts);
                },
                setShownImageId,
                setPreviewImageIds,
                archivedResidents,
                onTabAddRow: handleAddRow,
                isLastRow: index === receipts.length - 1,
                handleEntryRecurringItemCalculation,
                showAdditionalFees,
                showIncludedFees,
              });
            })}
            <Button
              variant="text"
              size="small"
              onClick={handleAddRow}
              startIcon={<Iconify icon={'ic:baseline-plus'} />}
            >
              {t('receipts.batch.form.add-receipt')}
            </Button>
          </div>
        )}
      </FieldArray>
    </Card>
  );
};
